import { currencyInfo } from './currency.ts';
import { money, type Money } from './money.ts';
import { divideRounded, type RoundingMode } from './rounding.ts';

/** An exact positive decimal as numerator / denominator (denominator is a power of ten). */
export type Decimal = {
  readonly numerator: bigint;
  readonly denominator: bigint;
};

const DECIMAL = /^(\d+)(?:\.(\d+))?$/;

/** Parses "1.1734" into 11734/10000 without touching floating point. */
export function parseDecimal(text: string): Decimal {
  const match = DECIMAL.exec(text.trim());
  if (!match) throw new RangeError(`Not a positive decimal: "${text}"`);
  const whole = match[1] as string;
  const fraction = match[2] ?? '';
  const denominator = 10n ** BigInt(fraction.length);
  const numerator = BigInt(whole + fraction);
  return { numerator, denominator };
}

/**
 * An exchange rate: 1 unit of `base` = `rate` units of `quote`.
 * Stored with its source and date so historical reports keep the rate they used.
 */
export type ExchangeRate = {
  readonly base: string;
  readonly quote: string;
  readonly rate: string;
  readonly date: string;
  readonly source: string;
};

export class RateCurrencyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RateCurrencyError';
  }
}

/**
 * Converts money with an explicit rate. Works in both directions of the rate
 * (GBP→PLN with a GBP/PLN rate, or PLN→GBP by dividing). The original amount is never
 * replaced by the converted one — callers keep both.
 */
export function convert(
  amount: Money,
  rate: ExchangeRate,
  target: string,
  mode: RoundingMode = 'HALF_EVEN',
): Money {
  if (amount.currency === target) return amount;
  const { numerator, denominator } = parseDecimal(rate.rate);
  if (numerator === 0n) throw new RangeError('Exchange rate must be positive');
  const fromScale = 10n ** BigInt(currencyInfo(amount.currency).scale);
  const toScale = 10n ** BigInt(currencyInfo(target).scale);

  if (rate.base === amount.currency && rate.quote === target) {
    // minor_to = minor_from / fromScale * rate * toScale
    return money(
      divideRounded(amount.minor * numerator * toScale, denominator * fromScale, mode),
      target,
    );
  }
  if (rate.quote === amount.currency && rate.base === target) {
    return money(
      divideRounded(amount.minor * denominator * toScale, numerator * fromScale, mode),
      target,
    );
  }
  throw new RateCurrencyError(
    `Rate ${rate.base}/${rate.quote} cannot convert ${amount.currency} to ${target}`,
  );
}
